import {FC, PropsWithChildren, ReactNode} from "react";
import {useTranslation} from "react-i18next";
import {useEnvironment} from "@common-core/react-runtime/context";
import {AlertSeverity} from "@interstate/components/Alert/Types/alertTypes";
import {Alert} from "@interstate/components/Alert";
import {Link} from "@interstate/components/Link";
import {Environment} from "../runtime";
import {isError} from "../utils";
import {
  type GraphqlErrorLike,
  isGraphqlErrorLike
} from "../amplify/hooks/types";
import {PageTemplate} from "./PageTemplate";
import {CoxAutoHeader} from "./CoxAutoHeader";
import {SidebarItem} from "./Sidebar";
import "./ErrorPage.scss";

export interface ErrorPageProps {
  id?: string;
  error: any;
  severity?: AlertSeverity;
  logo?: boolean;
  header?: ReactNode;
  banner?: ReactNode;
  sidebarItems?: SidebarItem[];
}

const getGraphqlErrorMessage = (
  error: GraphqlErrorLike
): {name: string; message: string} => {
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    const errors = error.graphQLErrors;
    const errorMessages = errors
      .map(err => {
        const code = err.extensions?.code ? ` (${err.extensions.code})` : "";
        return `${err.message}${code}`;
      })
      .join("; ");

    return {
      name: errors.length > 1 ? "GraphQL Errors" : "GraphQL Error",
      message: `${errorMessages}. Please contact support for assistance.`
    };
  }

  // Fallback for an error with no graphQLErrors detail
  return {
    name: "Error",
    message: error.message
  };
};

export const ErrorPage: FC<PropsWithChildren<ErrorPageProps>> = ({
  id = "error-page",
  error,
  severity = "caution",
  logo = true,
  header,
  banner,
  sidebarItems,
  children
}) => {
  const {t} = useTranslation();
  const environment = useEnvironment<Environment>();

  // Handle access denied
  if (error.message === "User is not assigned to the client application.") {
    return (
      <PageTemplate
        id={id}
        className={"error-page"}
        title={"Error"}
        header={(logo && <CoxAutoHeader />) || header}
        banner={banner}
        sidebarItems={sidebarItems}
        alert={
          <Alert id={"error"} type={severity}>
            {t("error-page.access-denied")}
            <Link
              id="link-to-landing-page"
              href={environment.landingPage}
              size={"sm"}
              visitedStyle={false}>
              {t("error-page.landing-page-link")}
            </Link>
          </Alert>
        }>
        {children}
      </PageTemplate>
    );
  }

  // Handle GraphQL errors (network and GraphQL errors — works for Apollo and Amplify)
  if (isGraphqlErrorLike(error)) {
    const {name, message} = getGraphqlErrorMessage(error);

    return (
      <PageTemplate
        id={id}
        className={"error-page graphql-error-page"}
        title={"Error"}
        header={(logo && <CoxAutoHeader />) || header}
        banner={banner}
        sidebarItems={sidebarItems}
        alert={
          <Alert id={"error"} type={"error"}>
            <span className={"error-name"}>{name}:</span>
            <span className={"error-message"}>{message}</span>
          </Alert>
        }>
        {children}
      </PageTemplate>
    );
  }

  // Handle a generic Error
  if (isError(error)) {
    return (
      <PageTemplate
        id={id}
        className={"error-page"}
        title={"Error"}
        header={(logo && <CoxAutoHeader />) || header}
        banner={banner}
        sidebarItems={sidebarItems}
        alert={
          <Alert id={"error"} type={severity}>
            <span className={"error-name"}>{error.name}:</span>
            <span className={"error-message"}>{error.message}</span>
          </Alert>
        }>
        {children}
      </PageTemplate>
    );
  }

  // Handle unknown error
  return (
    <PageTemplate
      id={id}
      className={"error-page"}
      title={"Error"}
      header={(logo && <CoxAutoHeader />) || header}
      banner={banner}
      sidebarItems={sidebarItems}
      alert={
        <Alert id={"error"} type={severity}>
          {error.toString()}
        </Alert>
      }>
      {children}
    </PageTemplate>
  );
};
